import { useEffect } from "react";
import RichardsonHeroSection from "./components/HeroSection";
import RichardsonStickyNav from "./components/StickyNav";
import RichardsonProcessingTimeSection from "./components/ProcessingTimeSection";
import RichardsonServicesSection from "./components/ServicesSection";
import RichardsonLocalAreaSection from "./components/LocalAreaSection";
import RichardsonTravelTimingSection from "./components/TravelTimingSection";
import RichardsonDestinationsSection from "./components/DestinationsSection";
import RichardsonTestimonialsSection from "./components/TestimonialsSection";
import RichardsonAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import RichardsonComparisonSection from "./components/ComparisonSection";
import RichardsonMistakesSection from "./components/MistakesSection";
import RichardsonHowItWorksSection from "./components/HowItWorksSection";
import RichardsonFAQSection, { faqs } from "./components/FAQSection";
import RichardsonHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import RichardsonFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function RichardsonPage() {
  useEffect(() => {
    document.title = "Richardson TX Passport Services — Expedited & Emergency | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/richardson/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Richardson, TX — as fast as 24 hours. Telecom Corridor tech professionals, UTD students, and Dallas County families. A+ BBB rated registered State Dept. courier. No office visit required.");
    setMeta("geo.placename", "Richardson, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "32.9483;-96.7299");
    setMeta("ICBM", "32.9483, -96.7299");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
          { "@type": "ListItem", position: 3, name: "Richardson Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center serves Richardson travelers remotely with emergency and expedited passport processing. Specializing in Telecom Corridor tech professionals, UTD students, and Dallas metro families.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Richardson", containedInPlace: { "@type": "State", name: "Texas" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "City", name: "Richardson" },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <RichardsonHeroSection />
      <RichardsonStickyNav />
      <RichardsonProcessingTimeSection />
      <RichardsonServicesSection />
      <RichardsonLocalAreaSection />
      <RichardsonTravelTimingSection />
      <RichardsonDestinationsSection />
      <RichardsonTestimonialsSection />
      <RichardsonAcceptanceFacilitiesSection />
      <RichardsonComparisonSection />
      <RichardsonMistakesSection />
      <RichardsonHowItWorksSection />
      <RichardsonFAQSection />
      <RichardsonHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/richardson" state="TX" />
      <RichardsonFinalCTA />
    </div>
  );
}